'use strict'
import React, { useMemo, useCallback } from 'react'
import { formatNumberValue } from '../../../../common/utils/NumberUtils'
import { reorderByAccMonth } from '../extra/helpers'
import { hasErrorClass } from '../extra/common'
import { useEmployeeCountDispatch, useEmployeeCountSelector } from '../extra/store'
import { ShowErrors } from './ShowErrors'
import BulkSetWorkingDays from './BulkSetWorkingDays'
import { shallowEqual } from 'react-redux'

/**
 * クライアントエラーオブジェクト
 *
 * @typedef {object} clientErrors
 *
 * @typedef {object | undefined} prescribedWorkingHoursMonthSetting
 * @property {(string, string)[]} messages - エラーメッセージ
 * @property {(string, string)[]} targets - エラー対象
 */
/**
 * @typedef {object} MonthlyWorkingDaysItem
 * @property {string} month
 * @property {string} workingDays
 * @property {string} prescribedWorkingHoursPerDay
 * @property {string} prescribedWorkingHoursPerMonth
 */
/*==================================
 カスタムフック
 ==================================*/
/**
 * 所定労働時間（月）設定に関するロジックを管理するカスタムフック
 *
 * @param {string} accPeriod - 決算月。
 * @returns {{
 *   prescribedWorkingHoursPerDay: string
 *   fiscalYearErrors: object
 *   reorderedColumns: { leftColumn: MonthlyWorkingDaysItem[]; rightColumn: MonthlyWorkingDaysItem[] }
 *   handleChangeWorkingHoursPerDay: (e: React.ChangeEvent<HTMLInputElement>) => void
 *   handleBlurWorkingHoursPerDay: (e: React.FocusEvent<HTMLInputElement>) => void
 *   handleChangeWorkingDays: (month: string) => (e: React.ChangeEvent<HTMLInputElement>) => void
 *   handleBlurWorkingDays: (month: string) => (e: React.FocusEvent<HTMLInputElement>) => void
 * }}
 */
const usePrescribedWorkingHoursLogic = (accPeriod) => {
  // 状態取得
  const dispatch = useEmployeeCountDispatch()
  const { prescribedWorkingHoursMonth, fiscalYearErrors } = useEmployeeCountSelector((state) => {
    const selectedYear = state.employeeCountInfo.selectedFiscalYear
    const fiscalYearSettings = state.employeeCountInfo.fiscalYearSettings[selectedYear]
    return {
      prescribedWorkingHoursMonth: fiscalYearSettings?.prescribedWorkingHoursMonth ?? [],
      fiscalYearErrors: fiscalYearSettings?.errors?.client ?? {}
    }
  }, shallowEqual)

  // 「所定労働時間／日」取得
  const prescribedWorkingHoursPerDay = prescribedWorkingHoursMonth?.[0]?.prescribedWorkingHoursPerDay ?? ''

  // 決算月に基づいて配列を並び替え
  const reorderedData = useMemo(
    () => reorderByAccMonth(prescribedWorkingHoursMonth, accPeriod),
    [prescribedWorkingHoursMonth, accPeriod]
  )

  // 6ヶ月分の所定労働時間情報を並べるため、配列を分割
  const reorderedColumns = useMemo(
    () => ({
      leftColumn: reorderedData.slice(0, 6),
      rightColumn: reorderedData.slice(6, 12)
    }),
    [reorderedData]
  )

  /**
   * 「所定労働時間／日」変更処理（変更アクションをdispatch）=> onChange
   *
   * @returns {(e: React.ChangeEvent<HTMLInputElement>) => void} - イベント（「所定労働時間／日」（入力値））
   */
  const handleChangeWorkingHoursPerDay = useCallback(
    (e) => {
      dispatch('CHANGE_WORKING_HOURS_PER_DAY', {
        prescribedWorkingHoursPerDay: e.target.value
      })
    },
    [dispatch]
  )

  /**
   * 「所定労働時間／日」変更処理（変更アクションをdispatch）=> onBlur
   *
   * @returns {(e: React.FocusEvent<HTMLInputElement>) => void} - イベント（「所定労働時間／日」（入力値））
   */
  const handleBlurWorkingHoursPerDay = useCallback(
    (e) => {
      // トリム処理
      const trimmedValue = e.target.value.trim()
      // 数値チェック、フォーマット変換
      const formattedValue = formatNumberValue(trimmedValue, 2, false)

      // フォーマットされた値をdispatch
      dispatch('CHANGE_WORKING_HOURS_PER_DAY', {
        prescribedWorkingHoursPerDay: formattedValue
      })
    },
    [dispatch]
  )

  /**
   * 営業日数変更処理（変更アクションをdispatch）=> onChange
   *
   * @param {string} month - 変更対象の年月（キー）
   * @returns {(e: React.ChangeEvent<HTMLInputElement>) => void} - イベント（営業日数（入力値））
   */
  const handleChangeWorkingDays = useCallback(
    (month) => (e) => {
      dispatch('CHANGE_WORKING_DAY', {
        month,
        workingDays: e.target.value
      })
    },
    [dispatch]
  )

  /**
   * 営業日数変更処理（変更アクションをdispatch）=> onBlur
   *
   * @param {string} month - 変更対象の年月（キー）
   * @returns {(e: React.FocusEvent<HTMLInputElement>) => void} - イベント（営業日数（入力値））
   */
  const handleBlurWorkingDays = useCallback(
    (month) => (e) => {
      // トリム処理
      const trimmedValue = e.target.value.trim()
      // 数値チェック、フォーマット変換
      const formattedValue = formatNumberValue(trimmedValue, 0, false)

      // フォーマットされた値をdispatch
      dispatch('CHANGE_WORKING_DAY', {
        month,
        workingDays: formattedValue
      })
    },
    [dispatch]
  )

  return {
    prescribedWorkingHoursPerDay,
    fiscalYearErrors,
    reorderedColumns,
    handleChangeWorkingHoursPerDay,
    handleBlurWorkingHoursPerDay,
    handleChangeWorkingDays,
    handleBlurWorkingDays
  }
}

/*==================================
 コンポーネント
 ==================================*/
/**
 * 各月の営業日数の入力行を表示するコンポーネント。
 *
 * @param {{
 *   item: MonthlyWorkingDaysItem
 *   clientErrors: clientErrors
 *   handleChangeWorkingDays: (month: string) => (e: React.ChangeEvent<HTMLInputElement>) => void
 *   handleBlurWorkingDays: (month: string) => (e: React.FocusEvent<HTMLInputElement>) => void
 * }} props
 * @returns {React.ReactElement}
 * @component
 */
const WorkingDaysRow = React.memo(({ item, clientErrors, handleChangeWorkingDays, handleBlurWorkingDays }) => {
  const {
    month = '',
    workingDays = '',
    prescribedWorkingHoursPerDay = '',
    prescribedWorkingHoursPerMonth = ''
  } = item || {}

  const monthMatch = month.match(/^(\d{1,2})月/)
  const monthNum = monthMatch ? parseInt(monthMatch[1], 10) : ''

  return (
    <div
      className={
        `grid grid-cols-[50px_max-content_repeat(2,_15px_75px)] ` +
        `justify-items-center items-center gap-3 mt-1 w-full text-base`
      }
    >
      <div className="font-semibold text-left text-right ">{month}</div>
      <div className="form-control">
        <input
          id={`working-day-${monthNum}`}
          name={`workingDays_${month}`}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="数値を入力"
          className={`text-input w-[185px] text-[14px] ${
            month && hasErrorClass(`workingDays_${month}`, clientErrors) ? 'is-error' : ''
          }`}
          value={workingDays}
          onChange={handleChangeWorkingDays(month)}
          onBlur={handleBlurWorkingDays(month)}
        />

        <span className="ml-3 inline-block align-middle">日</span>
      </div>
      <div>×</div>
      <div className="whitespace-nowrap justify-self-end">{prescribedWorkingHoursPerDay ?? '0'}時間</div>
      <div>=</div>
      <div className="whitespace-nowrap justify-self-end">{prescribedWorkingHoursPerMonth || '0'}時間</div>
    </div>
  )
})

/**
 * 所定労働時間（月）の設定セクション全体を管理するコンポーネント。
 *
 * このコンポーネントは、所定労働時間設定と関連するエラー表示をレンダリング
 *
 * @param {{ accPeriod: string }} props - 決算月
 * @returns {React.ReactElement}
 * @component
 */
const PrescribedWorkingHoursMonthSetting = (props) => {
  const { accPeriod } = props
  const {
    prescribedWorkingHoursPerDay,
    fiscalYearErrors,
    reorderedColumns,
    handleChangeWorkingHoursPerDay,
    handleBlurWorkingHoursPerDay,
    handleChangeWorkingDays,
    handleBlurWorkingDays
  } = usePrescribedWorkingHoursLogic(accPeriod)

  return (
    <>
      <div className="text-body-2-bold grow mb-2 ">■所定労働時間（月）の設定</div>
      <div className="mb-6">
        1日あたりの所定労働時間と各月の営業日数を設定してください。所定労働時間／月を算出して登録します。
        <br />
        以下のダッシュボードに適用されます。
        <br />
        1.1.2 年間平均
        <br />
        10.6.1{' '}
        <a className="text-blue-500" href="/master/dashboard/securitiesReportInfo" target="_blank">
          有価証券報告書レポート設定
        </a>
        の「臨時従業員の人数計算方式」で、計算対象が年間平均、計算方法がFTE（実労働時間換算）となっている場合
      </div>
      <div className="mb-5">
        <div className="text-body-2-medium grow mt-6 mb-2">所定労働時間／日</div>
        <div className="form-control">
          <input
            id="prescribed-working-hours-per-day"
            name="prescribedWorkingHoursPerDay"
            placeholder="数値を入力"
            type="text"
            className={`text-input format-digit w-[200px] text-[14px] ${hasErrorClass('prescribedWorkingHoursPerDay', fiscalYearErrors) ? 'is-error' : ''}`}
            value={prescribedWorkingHoursPerDay ?? ''}
            onChange={handleChangeWorkingHoursPerDay}
            onBlur={handleBlurWorkingHoursPerDay}
          />
          <span className="ml-3 inline-block">時間</span>
        </div>
      </div>

      <div className="flex items-center justify-between  mb-4 w-[90%]">
        <div className="text-body-2-medium">所定労働時間／月</div>
        <BulkSetWorkingDays isDisabled={false} />
      </div>
      <div className="px-4 py-4 bg-[rgba(49,80,165,0.03)] w-min min-w-[90%] flex gap-10">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <div className="text-sm font-medium whitespace-nowrap grid grid-cols-[50px_218px_repeat(2,_15px_75px)]  gap-3">
            <span className="col-[2/3]">営業日数</span>
            <span className="col-[3/-1] text-right">所定労働時間／月</span>
          </div>
          {reorderedColumns.leftColumn.map((item) => (
            <WorkingDaysRow
              key={item.month}
              item={item}
              clientErrors={fiscalYearErrors}
              handleChangeWorkingDays={handleChangeWorkingDays}
              handleBlurWorkingDays={handleBlurWorkingDays}
            />
          ))}
        </div>
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <div className=" text-sm font-medium whitespace-nowrap grid grid-cols-[50px_228px_repeat(2,_15px_75px)]  gap-3">
            <span className="col-[2/3]">営業日数</span>
            <span className="col-[3/-1] text-right">所定労働時間／月</span>
          </div>
          {reorderedColumns.rightColumn.map((item) => (
            <WorkingDaysRow
              key={item.month}
              item={item}
              clientErrors={fiscalYearErrors}
              handleChangeWorkingDays={handleChangeWorkingDays}
              handleBlurWorkingDays={handleBlurWorkingDays}
            />
          ))}
        </div>
      </div>

      <ShowErrors clientErrors={fiscalYearErrors} fieldName="prescribedWorkingHoursMonthSetting" />
    </>
  )
}

export default PrescribedWorkingHoursMonthSetting
